
import { Component, Watch } from "vue-property-decorator";
import RevisionsList from "@/components/Reusable/Revisions/RevisionsList.vue";
import Vue from "vue";
import {
  RevisionUiItem,
  PresetsRevision,
  PresetsRevisionsList
} from "@/types/revisions";
import { globalStore, uiStore } from "@/store/store-accessor";
import { getModuleNameFromMutation } from "@/store/util";
import defensiveCopy from "@/util/defensive-copy";
import { isEqual } from "lodash";
import { breakdanceUndo } from "@/lib/undo/undo";
import { fetchRemotePresetsRevisions } from "@/lib/presets/revisions";

// TODO refactor code duplication w/GlobalSettingsPanelHistoryViewMode
@Component({
  components: { RevisionsList }
})
export default class PresetsPanelHistoryViewMode extends Vue {
  isLoading = false;
  remoteRevisions: PresetsRevisionsList | null = null;
  selectedIndex: number | null = null;
  unsubscribeFromStore = () => {};

  currentUnsavedRevision: PresetsRevision | null = null;

  async beforeMount() {
    await this.loadRemoteRevisions();

    if (globalStore.presetsHasUnsavedChangesPresent) {
      this.recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch();

      /**
       * Without this condition, opening revisions list after Undoing/Redoing
       * revision selection will contain "Current" revision
       */
      if (this.selectedIndex === -1) {
        this.setCurrentStoreDataAsCurrentUnsavedRevisionIfDataIsNotEqualToMostRecentRemoteRevisionData();
      }
    }

    this.recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch();

    this.unsubscribeFromStore = this.$store.subscribe(mutationPayload => {
      const moduleName = getModuleNameFromMutation(mutationPayload);

      if (
        moduleName === "global" &&
        mutationPayload.type !== "global/restorePresetsRevision" &&
        mutationPayload.type !== "global/setGlobalSettings" // should be ignored
      ) {
        // Undo/Redo
        if (mutationPayload.type === "global/setPresets") {
          this.currentUnsavedRevision = null;
        } else {
          this.setCurrentStoreDataAsCurrentUnsavedRevisionIfDataIsNotEqualToMostRecentRemoteRevisionData();
        }

        this.recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch();
      }
    });
  }

  beforeDestroy() {
    this.unsubscribeFromStore();
  }

  async loadRemoteRevisions() {
    this.isLoading = true;
    this.remoteRevisions = await fetchRemotePresetsRevisions();
    this.selectedIndex = null;
    this.isLoading = false;
  }

  recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch() {
    this.selectedIndex = this.findSelectedIndexBasedOnStoreDataMatch();
  }

  findSelectedIndexBasedOnStoreDataMatch(): number {
    const currentPresets = defensiveCopy(globalStore.presets);

    return this.revisions.findIndex(revision => {
      return isEqual(revision.presets, currentPresets);
    });
  }

  setCurrentStoreDataAsCurrentUnsavedRevisionIfDataIsNotEqualToMostRecentRemoteRevisionData() {
    if (this.isCurrentStoreDataTheSameAsTheMostRecentRemoteRevisionData()) {
      this.currentUnsavedRevision = null;
    } else {
      this.setCurrentUnsavedRevisionFromCurrentStoreData();
    }
  }

  isCurrentStoreDataTheSameAsTheMostRecentRemoteRevisionData() {
    if (this.remoteRevisions === null) {
      return false;
    }

    const [mostRecentRemoteRevision] = this.remoteRevisions;
    const currentPresets = defensiveCopy(globalStore.presets);

    return isEqual(mostRecentRemoteRevision.presets, currentPresets);
  }

  setCurrentUnsavedRevisionFromCurrentStoreData() {
    this.currentUnsavedRevision = {
      id: -1,
      author: false,
      date: "Current",
      presets: defensiveCopy(globalStore.presets)
    };
  }

  get revisionUiItems(): RevisionUiItem<PresetsRevision>[] {
    return this.revisions.map(
      (
        revision: PresetsRevision,
        index: number
      ): RevisionUiItem<PresetsRevision> => ({
        isSelected: index === this.selectedIndex,
        revisionData: revision
      })
    );
  }

  get revisions(): PresetsRevision[] {
    const remoteRevisions = this.remoteRevisions ?? [];

    return this.currentUnsavedRevision
      ? [this.currentUnsavedRevision, ...remoteRevisions]
      : remoteRevisions;
  }

  onRevisionSelected(
    revisionUiItem: RevisionUiItem<PresetsRevision>,
    index: number
  ) {
    this.selectedIndex = index;

    breakdanceUndo.transaction(
      () => globalStore.restorePresetsRevision(revisionUiItem.revisionData),
      "Restore Presets From Revision"
    );
  }

  get unsavedChangesPresent() {
    return uiStore.unsavedChangesPresent;
  }

  @Watch("unsavedChangesPresent")
  async updateRevisionsAfterSaving() {
    if (!uiStore.unsavedChangesPresent) {
      await this.loadRemoteRevisions();
      this.currentUnsavedRevision = null;
      this.recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch();
    }
  }
}
